import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Typography,
  IconButton
} from '@material-ui/core';
import Utils from '../../common/utils';
import AetChaptersListStyle from './Styles/AetChaptersListStyle';
import Icon from '../icons/Icon';
import CheckboxOutlineIcon from '../icons/CheckboxOutlineIcon';
import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import { SYLLABUS_UI_CONSTANTS } from '../../common/constants';

const TreeViewCheckbox = ({
  data,
  selectedItems,
  onSelectionChange,
  parentApp,
  questionCounts,
  headerText,
  restrictAlreadyAssigned = true,
  textInfo,
  hasError,
  style = {},
  isInModal
}) => {
  const [expandedNodes, setExpandedNodes] = useState({});
  const isCheckboxClickRef = useRef(false);

  const classes = Utils.getSyllabusStyles(
    parentApp,
    { ereader: null, aet: AetChaptersListStyle },
    () => ({})
  );

  // this handles auto expand of nodes
  useEffect(() => {
    // Skip if this was triggered by a checkbox click
    if (isCheckboxClickRef.current) {
      isCheckboxClickRef.current = false;

      return;
    }
    if (!selectedItems || selectedItems.length === 0) return;
    const expandAncestorsOfSelected = (nodes, selectedIds, path = []) => {
      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        const isSelected = selectedIds.has(node.id);
        const newPath = [...path, node.id];

        if (isSelected) {
          path.forEach(ancestorId => {
            setExpandedNodes(prev => ({
              ...prev,
              [ancestorId]: true
            }));
          });
        }

        if (node.children && node.children.length > 0) {
          expandAncestorsOfSelected(node.children, selectedIds, newPath);
        }
      }
    };
    const selectedIds = new Set(selectedItems.map(item => (typeof item === 'object' ? item.id : item)
    ));
    expandAncestorsOfSelected(data, selectedIds);
  }, [selectedItems, data]);

  const toggleExpanded = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const isNodeSelected = (node) => {
    const nodeId = node.id;

    return selectedItems.some(selected => {
      if (typeof selected === 'object') {
        return selected.id === nodeId;
      }

      return selected === nodeId;
    });
  };

  const areAllDescendantsSelected = (node) => {
    if (!node.children || node.children.length === 0) {
      return isNodeSelected(node);
    }
    const isChildFullySelected = (child) => {
      if (!child.children || child.children.length === 0) {
        return isNodeSelected(child);
      }

      return areAllDescendantsSelected(child);
    };

    return node.children.every(child => isChildFullySelected(child));
  };

  /**
   * Checks if all unassigned descendants of a node are selected
   * @param {Object} node - The node to check
   * @returns {boolean} - True if all unassigned descendants are selected, false otherwise
   */
  const areAllUnassignedDescendantsSelected = (node) => {
    if (!node.children || node.children.length === 0) {
      if (node.isSectionAssigned) return true;

      return isNodeSelected(node);
    }

    return node.children.every(child => {
      // skip assigned nodes
      if (child.isSectionAssigned) return true;

      // For unassigned nodes with children, recursively check
      if (child.children && child.children.length > 0) {
        return areAllUnassignedDescendantsSelected(child);
      }

      // For unassigned leaf nodes, check if selected
      return isNodeSelected(child);
    });
  };

  const areSomeDescendantsSelected = (node) => {
    if (!node.children || node.children.length === 0) return false;
    const hasAnySelected = (n) => {
      if (isNodeSelected(n)) return true;
      if (n.children && n.children.length > 0) {
        return n.children.some(child => hasAnySelected(child));
      }

      return false;
    };

    const someSelected = node.children.some(child => hasAnySelected(child));
    if (!someSelected) return false;
    const allSelected = areAllDescendantsSelected(node);

    return !allSelected;
  };

  /**
   * Selects all descendants of a node
   * @param {Object} node - The node to select descendants of
   * @param {boolean} shouldSelect - True if descendants should be selected, false otherwise
   * @returns {void}
   */
  const selectAllDescendants = (node, shouldSelect) => {
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        const isChildSelected = isNodeSelected(child);
        // When deselecting, allow removing any node; when selecting, skip already-assigned nodes
        if (shouldSelect !== isChildSelected && (!shouldSelect || !child.isSectionAssigned)) {
          onSelectionChange(child);
        }

        selectAllDescendants(child, shouldSelect);
      });
    }
  };

  const handleNodeToggle = (node) => {
    isCheckboxClickRef.current = true;
    const hasChildren = node.children && node.children.length > 0;
    const allDescendantsSelected = areAllDescendantsSelected(node);
    const allUnassignedDescendantsSelected = areAllUnassignedDescendantsSelected(node);

    if (hasChildren) {
      if (allDescendantsSelected || allUnassignedDescendantsSelected) {
        selectAllDescendants(node, false);
        if (isNodeSelected(node)) {
          onSelectionChange(node);
        }
      } else {
        const isNodeCurrentlySelected = isNodeSelected(node);
        if (!isNodeCurrentlySelected) {
          onSelectionChange(node);
        }

        selectAllDescendants(node, true);
      }
    } else {
      onSelectionChange(node);
    }
  };

  const findTopLevelChapterId = (targetId, nodes = data, parentChapterId = null) => {
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const currentParentId = node.type === 'chapter' ? node.id : parentChapterId;

      if (node.id === targetId) {
        return currentParentId || parentChapterId;
      }

      if (node.children && node.children.length > 0) {
        const found = findTopLevelChapterId(targetId, node.children, currentParentId);

        if (found) return found;
      }
    }

    return null;
  };

  const calculateQuestionsForNode = (node) => {
    let totalQuestions = 0;

    if (!node.children || node.children.length === 0) {
      if (!Array.isArray(questionCounts)) return 0;

      const parentChapterId = findTopLevelChapterId(node.id);

      if (parentChapterId) {
        const chapterData = questionCounts.find(ch => ch.chapterId === parentChapterId);

        if (chapterData?.questions) {
          const nodeQuestions = chapterData.questions.filter(q => q.pageId === node.id);

          return nodeQuestions.length;
        }
      }

      return 0;
    }

    if (node.children) {
      node.children.forEach(child => {
        totalQuestions += calculateQuestionsForNode(child);
      });
    }

    return totalQuestions;
  };

  const getQuestionCountText = (node) => {
    const totalQuestions = calculateQuestionsForNode(node);

    return `${totalQuestions} ${textInfo.questionsLabel}`;
  };

  /**
   * Gets the tooltip text for a node
   * @param {boolean} isParentNode - True if the node is a parent node, false otherwise
   * @param {boolean} isAllDescendantsAssigned - True if all descendants are assigned, false otherwise
   * @param {boolean} hasSomeAssignedDescendants - True if some descendants are assigned, false otherwise
   * @returns {string} - The tooltip text
   */
  const getTooltipText = (isParentNode, isAllDescendantsAssigned, hasSomeAssignedDescendants) => {
    let tooltipText = null;
    if (isParentNode && isAllDescendantsAssigned) {
      tooltipText = textInfo.includedInAnotherAssignmentTooltip;
    } else if (isParentNode && hasSomeAssignedDescendants) {
      tooltipText = textInfo.someSectionsIncludedInAnotherAssignmentTooltip;
    } else if (isAllDescendantsAssigned) {
      tooltipText = textInfo.includedInAnotherAssignmentTooltip;
    }

    return tooltipText;
  };

  const renderTreeNode = (node, level = 0) => {
    const nodeId = node.id;
    const nodeTitle = node.title;
    const children = node.children || [];
    const isExpanded = expandedNodes[nodeId];

    const hasChildren = children && children.length > 0;
    const isParentNode = hasChildren;
    const isTopLevel = level === 0;

    const allDescendantsSelected = areAllDescendantsSelected(node);
    const isChecked = allDescendantsSelected;

    const isIndeterminate = isParentNode && !isChecked && areSomeDescendantsSelected(node);

    const containerClass = isTopLevel ? classes.optionItem : classes.moduleRow;
    const rowClass = isTopLevel ? classes.chapterRow : classes.moduleRow;
    const labelClass = isTopLevel ? classes.textLabel : classes.moduleTextLabel;
    const checkboxClass = classes.customCheckbox;
    const contentWrapperClass = isTopLevel ? classes.contentWrapper : classes.moduleContentWrapper;
    const isAllDescendantsAssigned = restrictAlreadyAssigned && Utils.isAllDescendantsAssigned(node);
    const hasSomeAssignedDescendants = restrictAlreadyAssigned && Utils.hasSomeAssignedDescendants(node);
    const tooltipText = getTooltipText(
      isParentNode,
      isAllDescendantsAssigned,
      hasSomeAssignedDescendants
    );

    // Fix the disabled logic to work for parent nodes at any level
    const isDisabled =
      restrictAlreadyAssigned &&
      (isParentNode ? isAllDescendantsAssigned : Utils.isNodeOrDescendantAssigned(node) && !isChecked);

    const determineToSelectDeselect = isChecked && !isDisabled;
    const determineToPartiallySelect = isIndeterminate;

    return (
      <div key={nodeId} className={containerClass}>
        <div className={`${rowClass} ${classes.commonIndent} ${isExpanded ? 'expanded' : ''}`}>
          <div className={contentWrapperClass}>
            {children && children.length > 0 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(nodeId);
              }}
              className={classes.expandButton}
              size="small"
            >
              {isExpanded ? <Icon name="CaretDownIcon" /> : <Icon name="CaretUpIcon" />}
            </IconButton>
            )}
            {(!children || children.length === 0) && <div className={classes.expandButtonSpacer} />}

            <TooltipWrapper
              showTooltip={Boolean(tooltipText)}
              tooltip={tooltipText || ''}
              placement="top-start"
              classes={{
                tooltipPlacementTop: classes.tooltipPlacementTop
              }}
              popperProps={{
                modifiers: {
                  offset: {
                    offset: '24px, 0',
                    enabled: true
                  }
                }
              }}
            >
              <div className={classes.checkboxWrapper}>
                <Checkbox
                  checked={determineToSelectDeselect}
                  indeterminate={determineToPartiallySelect}
                  disabled={isDisabled}
                  onChange={() => {
                    handleNodeToggle(node);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  color="primary"
                  disableRipple
                  className={checkboxClass}
                  icon={<CheckboxOutlineIcon className={classes.checkboxIcon} />}
                  checkedIcon={<CheckboxCheckedIcon />}
                  inputProps={{
                    'aria-labelledby': `select-${nodeId}`
                  }}
                />
                <Typography id={`select-${nodeId}`} className={`${labelClass} ${isDisabled ? classes.disabledText : ''} ${isInModal ? classes.modalTextLabel : ''}`}>
                  {nodeTitle}
                </Typography>
              </div>
            </TooltipWrapper>

            {questionCounts && data && (
            <Typography className={classes.questionCount}>
              {getQuestionCountText(node)}
            </Typography>
            )}
          </div>

          <div className={`${isExpanded ? classes.expandedWrapper : ''}`}>
            {children && children.length > 0 && isExpanded && (
              children.map(child => renderTreeNode(child, level + 1))
            )}
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className={classes.tocListContainer}>
      {headerText && (
        <Typography className={classes.paragraph}>
          {headerText}
        </Typography>
      )}
      <CustomScrollbar
        classes={{ scrollBarContainer: `${classes.checkboxContainer} ${hasError ? classes.checkboxContainerError : ''}` }}
        autoHeight
        autoHeightMin={style?.minHeight || SYLLABUS_UI_CONSTANTS.CHECKBOX_CONTAINER_MIN_HEIGHT}
        autoHeightMax={style?.maxHeight || SYLLABUS_UI_CONSTANTS.CHECKBOX_CONTAINER_MAX_HEIGHT}
      >
        {data.map(node => renderTreeNode(node))}
      </CustomScrollbar>
    </div>
  );
};

TreeViewCheckbox.propTypes = {
  data: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      children: PropTypes.array
    })
  ])).isRequired,
  selectedItems: PropTypes.array,
  onSelectionChange: PropTypes.func.isRequired,
  parentApp: PropTypes.string,
  questionCounts: PropTypes.array,
  headerText: PropTypes.string,
  restrictAlreadyAssigned: PropTypes.bool,
  textInfo: PropTypes.object,
  hasError: PropTypes.bool,
  style: PropTypes.object,
  isInModal: PropTypes.bool
};

TreeViewCheckbox.defaultProps = {
  parentApp: '',
  questionCounts: [],
  headerText: '',
  selectedItems: [],
  restrictAlreadyAssigned: true,
  textInfo: {},
  hasError: false,
  style: {},
  isInModal: false
};

export default TreeViewCheckbox;
